import { ratingShotXg, ratingShotOutcome, recordedShotXg } from './ratingShotEvidence.js';
import { ratingPossessionEvidence } from './ratingPossessionValue.js';

// Haxball Impact v6: one deterministic, auditable rating for every view.
// Expert weights, not a model trained against independent human labels.
export const RATING_VERSION = 'haxball-impact-v6';
export const RATING_BASELINE = 6.5;
const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
const number = value => typeof value === 'number' && Number.isFinite(value) ? value : 0;
const count = value => Math.max(0, number(value));
const rounded = n => Math.round(n * 1000) / 1000 || 0;
const reward = (value, weight, limit) => limit * (1 - Math.exp(-count(value) * weight / limit));
const name = value => typeof value === 'string' ? value.trim().toLowerCase() : '';

// The xG model is already used by replay analysis. Goal credit uses a small,
// symmetric adjustment around a neutral 0.50 xG: a 0.10 xG finish is worth
// slightly more than a routine 0.90 xG finish, while missing xG stays neutral.
const goalDifficultyDelta = xg => 0.3 * (0.5 - clamp(xg, 0, 1));
const shotXgValue = (shot, options) => ratingShotXg(shot, options).xg;

function calculateConfidence({ metrics = {}, evidence = {}, observedActions = 0 }) {
  const actionCoverage = clamp(Math.log1p(Math.max(0, observedActions)) / Math.log1p(40), 0, 1);
  const metricCoverage = [
    metrics.shotsTotal,
    metrics.passesCompleted,
    metrics.possessionTouches,
    metrics.duelsTotal,
    metrics.recoveries,
    metrics.positionSamples
  ].filter(value => count(value) > 0).length / 6;
  const linkedEvidence = clamp(
    (count(evidence.goalDifficultyKnown) + count(evidence.expectedAssistKnown) + count(evidence.possessionSequences)) / 24,
    0,
    1
  );
  return rounded(clamp(0.25 + actionCoverage * 0.45 + metricCoverage * 0.2 + linkedEvidence * 0.1, 0.2, 0.98));
}

// Legacy "keyPasses" counts long forward passes, not passes leading to shots.
// Explicit shot-assist links or a completed pass immediately before a teammate
// shot qualify as chance creation. The legacy xG-prevented field allocates team xG by occupancy, so it is not keeper credit.
export function ratingEventEvidence(match, analysis = null) {
  const evidence = new Map();
  const get = nick => {
    const key = name(nick);
    if (!evidence.has(key)) evidence.set(key, {
      chancesCreated: 0,
      saveQuality: 0,
      linkedSaves: 0,
      ownGoals: 0,
      ownGoalsInScorerCount: 0,
      goalDifficultyDelta: 0,
      goalDifficultyKnown: 0,
      goalXgTotal: 0,
      shotOutcomeResidual: 0,
      shotOutcomeKnown: 0,
      missedOnTargetXg: 0,
      expectedAssists: 0,
      expectedAssistKnown: 0,
      nonGoalChanceXg: 0,
      nonGoalChanceKnown: 0,
      assistGoalXg: 0,
      assistGoalKnown: 0,
      possessionValue: 0,
      pressureRelief: 0,
      possessionSequences: 0
    });
    return evidence.get(key);
  };
  const shots = Array.isArray(match?.shots) ? match.shots : [];
  const shotOptions = {
    stadium: match?.stadium || null,
    redTeam: Array.isArray(match?.redTeam) ? match.redTeam : [],
    blueTeam: Array.isArray(match?.blueTeam) ? match.blueTeam : []
  };
  const shotKeys = new Set();
  const tick = event => {
    const value = event?.matchTick ?? event?.tick;
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  };
  const kickKey = event => `${name(event?.player)}:${tick(event)}`;
  const kicks = (Array.isArray(match?.kicks) ? match.kicks : []).filter(event => tick(event) !== null).slice().sort((a, b) => tick(a) - tick(b));
  const kickIndex = new Map(kicks.map((event, index) => [kickKey(event), index]));
  const completed = new Set((Array.isArray(match?.passes) ? match.passes : []).filter(pass => tick(pass?.lastShot) !== null).map(pass => kickKey(pass.lastShot)));
  const linkedCreator = shot => {
    const index = kickIndex.get(kickKey(shot));
    if (index === undefined || index === 0 || tick(shot) === null) return null;
    let previous = index - 1;
    while (previous > 0 && name(kicks[previous].player) === name(shot.player)) previous--;
    const kick = kicks[previous];
    if (name(kick.player) === name(shot.player) || !name(shot.team) || name(kick.team) !== name(shot.team)) return null;
    if (tick(shot) - tick(kick) > 240 || !completed.has(kickKey(kick))) return null;
    return kick.player;
  };
  shots.forEach((shot, index) => {
    if (!shot) return;
    const key = `${name(shot.player)}:${shot.matchTick ?? shot.tick ?? index}`;
    if (shotKeys.has(key)) return;
    shotKeys.add(key);
    const creator = (typeof shot.assist === 'string' ? shot.assist : shot.assist?.player) || linkedCreator(shot);
    const shotXg = shotXgValue(shot, shotOptions);
    const outcome = ratingShotOutcome(shot);
    const isGoal = outcome === 'goal';
    const isKnownOnTargetMiss = outcome === 'on_target_no_goal';
    const shooterEvidence = get(shot.player);
    if (shotXg !== null && (isGoal || isKnownOnTargetMiss)) {
      shooterEvidence.shotOutcomeResidual += isGoal ? 1 - shotXg : -shotXg;
      shooterEvidence.shotOutcomeKnown++;
      if (isKnownOnTargetMiss) shooterEvidence.missedOnTargetXg += shotXg;
    }
    if (name(creator) && name(creator) !== name(shot.player)) {
      const creatorEvidence = get(creator);
      if (!isGoal) creatorEvidence.chancesCreated++;
      if (shotXg !== null) {
        creatorEvidence.expectedAssists += shotXg;
        creatorEvidence.expectedAssistKnown++;
        if (isGoal) {
          creatorEvidence.assistGoalXg += shotXg;
          creatorEvidence.assistGoalKnown++;
        } else {
          creatorEvidence.nonGoalChanceXg += shotXg;
          creatorEvidence.nonGoalChanceKnown++;
        }
      }
    }
  });
  const saveKeys = new Set();
  (Array.isArray(match?.saves) ? match.saves : []).forEach(save => {
    if (!save || !name(save.player) || !Number.isInteger(save.shotIndex)) return;
    const shot = shots[save.shotIndex];
    const key = `${name(save.player)}:${shot && tick(shot) !== null ? kickKey(shot) : save.shotIndex}`;
    if (!shot || saveKeys.has(key) || ratingShotOutcome(shot) === 'goal' || ratingShotOutcome(shot) === 'off_target' || shot.onTarget === false) return;
    saveKeys.add(key);
    // Quality only supplements the save count. It never allocates team xG.
    const xg = shotXgValue(shot, shotOptions);
    if (xg !== null) {
      get(save.player).saveQuality += xg;
      get(save.player).linkedSaves++;
    }
  });

  // Match goals and shot records are produced by different parts of the
  // replay parser. Link them conservatively by explicit index first, then by
  // scorer and nearest tick. If no reliable shot xG exists, leave the goal at
  // the neutral finishing value instead of inventing difficulty.
  const usedGoalShots = new Set();
  const goalTick = goal => {
    const values = [goal?.shot?.matchTick, goal?.shot?.tick, goal?.gameTicks, goal?.matchTick];
    for (const value of values) {
      if (typeof value === 'number' && Number.isFinite(value)) return value;
    }
    return null;
  };
  const findGoalShot = (goal, scorer) => {
    const scorerKey = name(scorer);
    const validScorer = shot => name(shot?.player ?? shot?.scorer) === scorerKey;
    const toIndex = value => Number.isInteger(Number(value)) ? Number(value) : null;
    const explicitShotIndex = toIndex(goal?.shotIndex ?? goal?.shot?.shotIndex);
    if (explicitShotIndex !== null && validScorer(shots[explicitShotIndex]) && !usedGoalShots.has(explicitShotIndex)) {
      usedGoalShots.add(explicitShotIndex);
      return shots[explicitShotIndex];
    }
    const explicitGoalIndex = toIndex(goal?.goalIndex ?? goal?.shot?.goalIndex);
    if (explicitGoalIndex !== null) {
      const indexed = shots.findIndex((shot, index) => !usedGoalShots.has(index) && validScorer(shot) && toIndex(shot?.goalIndex) === explicitGoalIndex);
      if (indexed >= 0) {
        usedGoalShots.add(indexed);
        return shots[indexed];
      }
    }
    const targetTick = goalTick(goal);
    const candidates = shots
      .map((shot, index) => ({ shot, index }))
      .filter(({ shot, index }) => !usedGoalShots.has(index) && validScorer(shot) && ratingShotOutcome(shot) === 'goal'
        && targetTick !== null && tick(shot) !== null && Math.abs(tick(shot) - targetTick) <= 600)
      .sort((a, b) => {
        if (targetTick === null) return a.index - b.index;
        const tick = item => {
          const value = item?.shot?.matchTick ?? item?.shot?.tick;
          return typeof value === 'number' && Number.isFinite(value) ? value : Infinity;
        };
        return Math.abs(tick(a) - targetTick) - Math.abs(tick(b) - targetTick);
      });
    const selected = candidates[0];
    if (!selected) return null;
    usedGoalShots.add(selected.index);
    return selected.shot;
  };
  (Array.isArray(match?.goals) ? match.goals : []).forEach(goal => {
    const scorer = typeof goal?.scorer === 'string' ? goal.scorer.trim() : '';
    if (!scorer || / \(own goal\)$/i.test(scorer) || goal.ownGoal === true) return;
    const linkedShot = findGoalShot(goal, scorer);
    const xg = recordedShotXg(goal?.shot) ?? shotXgValue(linkedShot, shotOptions) ?? recordedShotXg(goal);
    if (xg === null) return;
    const playerEvidence = get(scorer);
    playerEvidence.goalDifficultyDelta += goalDifficultyDelta(xg);
    playerEvidence.goalDifficultyKnown += 1;
    playerEvidence.goalXgTotal += xg;
  });

  (Array.isArray(match?.goals) ? match.goals : []).forEach(goal => {
    const scorer = typeof goal?.scorer === 'string' ? goal.scorer.trim() : '';
    const legacyOwnGoal = / \(own goal\)$/i.test(scorer);
    if (!scorer || (!legacyOwnGoal && goal.ownGoal !== true)) return;
    const player = legacyOwnGoal ? scorer.replace(/ \(own goal\)$/i, '') : scorer;
    get(player).ownGoals++;
    // The legacy suffix is already excluded from the real player's goal tally.
    // Explicit boolean records, however, are counted by summarizeGoals.
    if (!legacyOwnGoal) get(player).ownGoalsInScorerCount++;
  });

  // Event-level pass evidence avoids rewarding unpressured touches or
  // charging a turnover again after it already entered the loss metrics.
  ratingPossessionEvidence(match, analysis).forEach((value, nick) => {
    Object.assign(get(nick), value);
  });
  return evidence;
}

export function rateHaxballPlayer(summary, { minutes = 10, evidence = {} } = {}) {
  const m = summary?.metrics || {};
  const role = ['keeper', 'defender', 'midfielder', 'forward'].includes(summary?.role) ? summary.role : 'midfielder';
  const roleWeights = {
    keeper: { defense: 1.1, creation: 0.9, progression: 0.85, finishing: 0.7, assisting: 0.85, threat: 0.65, saves: 1.15 },
    defender: { defense: 1.1, creation: 1, progression: 0.95, finishing: 0.85, assisting: 0.9, threat: 0.8, saves: 0.9 },
    midfielder: { defense: 1, creation: 1.1, progression: 1.1, finishing: 1, assisting: 1.05, threat: 0.95, saves: 0.9 },
    forward: { defense: 1, creation: 1, progression: 0.98, finishing: 1.1, assisting: 1.05, threat: 1.15, saves: 0.8 }
  }[role];
  const duration = Math.max(0, number(minutes));
  const recordedMinutes = summary?.player?.minutesPlayed ?? summary?.player?.metrics?.minutesPlayed;
  const hasMinutes = typeof recordedMinutes === 'number' && Number.isFinite(recordedMinutes) && recordedMinutes >= 0;
  const playedMinutes = hasMinutes ? Math.min(recordedMinutes, duration) : duration;
  // Long recordings cannot accumulate unlimited credit; short cameos are never
  // extrapolated to a full match. Unknown individual minutes remain explicit.
  const volumeScale = Math.sqrt(10 / Math.max(10, duration));
  const reliability = 0.35 + 0.65 * clamp(playedMinutes / 3, 0, 1);
  const contributions = [];
  const add = (key, label, value) => {
    contributions.push({ key, label, value: rounded(value * reliability) });
  };
  const goals = Math.max(0, count(m.goals) - count(evidence.ownGoalsInScorerCount));
  const adjustedGoalUnits = clamp(goals + number(evidence.goalDifficultyDelta), goals * 0.8, goals * 1.2);
  add('finishing', 'Goals', reward(adjustedGoalUnits * volumeScale, 0.85 * roleWeights.finishing, 2.5));
  const assistGoalKnown = count(evidence.assistGoalKnown);
  const assistGoalAverageXg = assistGoalKnown > 0 ? number(evidence.assistGoalXg) / assistGoalKnown : null;
  const knownAssists = Math.min(count(m.assists), assistGoalKnown);
  const assistUnits = count(m.assists) + knownAssists * (assistGoalAverageXg === null ? 0 : clamp((assistGoalAverageXg - 0.5) * 0.4, -0.2, 0.2));
  add('assisting', 'Assists', reward(assistUnits * volumeScale, 0.55 * roleWeights.assisting, 1.4));
  const created = count(evidence.chancesCreated);
  const expectedAssistKnown = count(evidence.expectedAssistKnown);
  const knownChances = Math.min(created, count(evidence.nonGoalChanceKnown));
  const chanceQuality = knownChances > 0 ? clamp(count(evidence.nonGoalChanceXg) / count(evidence.nonGoalChanceKnown), 0, 1) : 0.5;
  const chanceUnits = created + knownChances * (chanceQuality - 0.5) * 0.4;
  add('creation', 'Chances created', reward(chanceUnits * volumeScale, 0.15 * roleWeights.creation, 0.65));
  // A saved/missed shot earns little; high-volume shooting alone cannot earn a
  // top rating. A verified high-quality miss can reduce this contribution
  // even when the player scored no goals. Pending shots stay neutral.
  const nonGoalShots = Math.max(0, count(m.shotsOnTarget) - goals);
  const missedChancePenalty = reward(count(evidence.missedOnTargetXg) * volumeScale, 0.12, 0.4);
  add('threat', 'Other shots on target', reward(nonGoalShots * volumeScale, 0.07 * roleWeights.threat, 0.3) - missedChancePenalty);
  const passes = count(m.passesCompleted);
  const progressive = Math.min(passes, count(m.progressivePasses));
  const pressureEscapes = Math.min(8, count(evidence.pressureRelief));
  const positivePossessionValue = Math.min(5, Math.max(0, number(evidence.possessionValue)));
  add('progression', 'Progressive passing', reward((progressive + positivePossessionValue * 0.35) * volumeScale, 0.022 * roleWeights.progression, 0.35));
  add('circulation', 'Completed passes', reward((passes + pressureEscapes) * volumeScale, 0.003, 0.12));
  // Recoveries, interceptions and forced turnovers frequently describe the
  // same transition. Take their maximum, rather than awarding three bonuses.
  const recoveries = Math.max(count(m.recoveries), count(m.interceptions), count(m.forcedTurnovers));
  // Legacy lastDefenderEvents counts possession losers, and sweeperActions
  // counts kicks by pitch location. Neither establishes a defensive success.
  const defensiveSequenceValue = Math.min(recoveries, count(m.finalThirdRecoveries)) * 0.1;
  add('recoveries', 'Ball recoveries', reward((recoveries + defensiveSequenceValue) * volumeScale, 0.045 * roleWeights.defense, 0.6));
  add('clearances', 'Clearances', reward(count(m.clearances) * volumeScale, 0.022 * roleWeights.defense, 0.3));
  // Bayesian smoothing: one won duel must not be treated as a perfect match.
  const won = count(m.duelsWon), lost = count(m.duelsLost);
  const duelEvidence = won + lost;
  const duelRate = (won + 3) / (duelEvidence + 6);
  add('duels', 'Duel outcomes', (duelRate - 0.5) * 1.5 * (1 - Math.exp(-duelEvidence / 12)));
  add('saves', 'Saves', reward(count(m.saves) * volumeScale, 0.18 * roleWeights.saves, 1.25));
  add('saveQuality', 'Linked save difficulty', reward(count(evidence.saveQuality) * volumeScale, 0.1 * roleWeights.saves, 0.3));
  // Dangerous losses are already losses. Penalize them once at the higher rate;
  // ordinary turnover credit is deliberately conservative for legacy replays.
  const dangerous = count(m.dangerousTurnovers);
  const ordinary = Math.max(0, count(m.turnoversCommitted) - dangerous);
  add('turnovers', 'Other possession losses', -reward(ordinary * volumeScale, 0.015, 0.55));
  add('errors', 'Dangerous possession losses', -reward(dangerous * volumeScale, 0.38, 1.8));
  add('ownGoals', 'Own goals', -reward(count(evidence.ownGoals), 0.8, 1.8));
  const raw = RATING_BASELINE + contributions.reduce((sum, item) => sum + item.value, 0);
  const rating = Math.round(clamp(raw, 3, 10) * 10) / 10;
  contributions.push({ key: 'rounding', label: 'Rounding and rating limit', value: rounded(rating - raw) });
  const active = ['goals', 'assists', 'shotsTotal', 'saves', 'passesCompleted', 'touches', 'possessionTouches', 'recoveries', 'clearances', 'duelsWon', 'duelsLost', 'gkTicks', 'positionSamples', 'turnoversCommitted', 'dangerousTurnovers'].some(key => count(m[key]) > 0) || count(evidence.ownGoals) > 0;
  const observedActions = goals + count(m.assists) + passes + duelEvidence + count(m.saves) + recoveries;
  const provisional = playedMinutes < 2 || observedActions < 5;
  return {
    version: RATING_VERSION, rating, baseline: RATING_BASELINE, role, active,
    provisional, evidenceLevel: provisional ? 'limited' : 'standard',
    playedMinutes: hasMinutes ? playedMinutes : null,
    goalDifficulty: {
      knownGoals: count(evidence.goalDifficultyKnown),
      averageXg: count(evidence.goalDifficultyKnown) > 0 ? number(evidence.goalXgTotal) / count(evidence.goalDifficultyKnown) : null,
      adjustedGoalUnits,
      missedChancePenalty,
      shotOutcomeResidual: number(evidence.shotOutcomeResidual),
      shotOutcomeKnown: count(evidence.shotOutcomeKnown)
    },
    possessionValue: number(evidence.possessionValue),
    pressureReliefActions: count(evidence.pressureRelief),
    chanceQuality: {
      chancesCreated: created,
      expectedAssists: expectedAssistKnown > 0 ? number(evidence.expectedAssists) : null,
      expectedAssistKnown,
      assistGoalAverageXg,
      assistGoalKnown
    },
    confidence: calculateConfidence({ metrics: m, evidence, observedActions }),
    contributions,
    notes: [
      ...(!hasMinutes ? ['Individual playing time unavailable; match duration used without extrapolating cameo statistics.'] : []),
      ...(provisional ? ['Limited playing time or recorded actions; treat this rating as provisional.'] : []),
      'Goals use linked shot difficulty. Other shots on target include capped missed-chance costs. Raw xG is an uncalibrated difficulty signal. Linked passes determine creation and pressure relief; occupancy-based xG prevented does not affect the rating.'
    ]
  };
}
